import React from "react";
import ReactDOM from "react-dom";
import App from "./App";
import { ClerkProvider, useUser } from "@clerk/clerk-react";
import { BrowserRouter, useLocation } from "react-router-dom";
import axios from "axios";
import posthog from "posthog-js";
import * as Sentry from "@sentry/react";

import { PostHogProvider } from "posthog-js/react";

import "./assets/styles/main.css";
import "./assets/styles/responsive.css";
import "./styles/colors.css";
import "./styles/dashboardCardTokens.css";
import "./global.css";

// Global Axios interceptor for session expiration
axios.interceptors.response.use(
  response => response,
  error => {
    if (error?.response?.status === 401) {
      localStorage.removeItem("clerkToken");
      if (window.Clerk?.signOut) {
        window.Clerk.signOut();
      }
      window.location.href = "/sign-in";
    }
    return Promise.reject(error);
  }
);

const clerkPubKey =
  process.env.REACT_APP_CLERK_PUBLISHABLE_KEY ||
  "pk_test_Y2FwYWJsZS1sYWItNDAuY2xlcmsuYWNjb3VudHMuZGV2JA";

const posthogApiKey =
  process.env.REACT_APP_POSTHOG_KEY ||
  process.env.VITE_PUBLIC_POSTHOG_KEY ||
  "phc_th4OUCYwWB5ycg1J7FI8yzOcwU9nu2TFa0zhEEQPtxH";

const posthogHost =
  process.env.REACT_APP_POSTHOG_HOST ||
  process.env.VITE_PUBLIC_POSTHOG_HOST ||
  "https://us.i.posthog.com";

// Initialize Sentry (production only)
const sentryDsn = process.env.REACT_APP_SENTRY_DSN;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: process.env.NODE_ENV,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllInputs: false,
        blockAllMedia: false,
      }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

// Initialize PostHog
if (posthogApiKey) {
  posthog.init(posthogApiKey, {
    api_host: posthogHost,
    defaults: '2025-11-30',
    autocapture: true,
    session_recording: {
      maskAllInputs: false,
      maskAllText: false,
      recordHeaders: true,
    },
  });
}

const posthogOptions = {
  api_host: posthogHost,
  autocapture: true,
  session_recording: {
    maskAllInputs: false,
    maskAllText: false,
    recordHeaders: true,
  },
};

const rootEl = document.getElementById("root");
const appContent = (
  <ClerkProvider publishableKey={clerkPubKey} afterSignOutUrl="/sign-in">
    <Sentry.ErrorBoundary fallback={<div>Something went wrong. Please refresh the page.</div>}>
      <BrowserRouter>
        <PostHogClerkIdentifier />
        <App />
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  </ClerkProvider>
);

const app = posthogApiKey ? (
  <PostHogProvider apiKey={posthogApiKey} options={posthogOptions}>
    {appContent}
  </PostHogProvider>
) : (
  appContent
);

function PostHogClerkIdentifier() {
  const { user } = useUser();
  const location = useLocation();
  const hasStartedRef = React.useRef(false);

  React.useEffect(() => {
    if (!posthogApiKey) return;
    posthog?.stopSessionRecording?.();
  }, []);

  React.useEffect(() => {
    if (!posthogApiKey) return;

    if (user) {
      const userId = user.id;
      const email = user.primaryEmailAddress?.emailAddress;
      const name = user.fullName;

      posthog.identify(userId, {
        email,
        name,
      });
    } else {
      posthog.reset();
    }
  }, [user]);

  React.useEffect(() => {
    if (!posthogApiKey) return;

    const onDashboard = location.pathname.startsWith("/dashboard");

    if (user && onDashboard && !hasStartedRef.current) {
      posthog?.startSessionRecording?.();
      hasStartedRef.current = true;
    }
  }, [location.pathname, user]);

  return null;
}

// React 18+ createRoot, else fallback for React 17
const maybeCreateRoot = ReactDOM.createRoot;
if (typeof maybeCreateRoot === "function") {
  // @ts-ignore - for projects without React 18 types
  const root = ReactDOM.createRoot(rootEl);
  root.render(app);
} else {
  ReactDOM.render(app, rootEl);
}
